/**
 * @file Extract schedule for one attachment version: reads its bytes through
 * the Download path (`downloadAttachment` with `returnBlob`, chunked versions
 * included), starts PDF intake with that version as the job's `source`, and
 * opens the schedule review. Consumers: `Attachments` (the rows) and
 * `AttachmentPreviewOverlay` (its header), each with its own message api.
 *
 * The source names the version actually read, which the reader reports, so a
 * request for the latest version records the number it resolved to. A cancelled
 * read reports nothing. The router is imported on click rather than reached
 * through `useNavigate`, so the panel and overlay still render without a Router.
 */
import { useCallback, useContext, useState } from 'react';
import type { MessageInstance } from 'antd/es/message/interface';
import type { PdfIntakeOrderRef } from '#types';
import { UserContext } from '@/providers/UserContext';
import { startPdfIntake } from '@/services/startPdfIntake';
import { useAttachmentDownload } from './useAttachmentDownload';

export const useExtractSchedule = (
  messageApi: MessageInstance,
): {
  extractSchedule: (params: {
    fileUid: string;
    versionId: string;
    fileName: string;
    order: PdfIntakeOrderRef;
  }) => Promise<void>;
  /** The version being read and sent, or null. */
  extractingVersionId: string | null;
  /** The user holds aiOperator or aiAdmin. */
  canExtract: boolean;
} => {
  const { user, userRights } = useContext(UserContext);
  const { downloadAttachment } = useAttachmentDownload();
  const [extractingVersionId, setExtractingVersionId] = useState<string | null>(
    null,
  );
  const canExtract = !!user && !!(userRights.aiOperator || userRights.aiAdmin);

  const extractSchedule = useCallback(
    async ({
      fileUid,
      versionId,
      fileName,
      order,
    }: {
      fileUid: string;
      versionId: string;
      fileName: string;
      order: PdfIntakeOrderRef;
    }) => {
      if (!user) return;
      setExtractingVersionId(versionId);
      try {
        const result = await downloadAttachment({
          fileUid,
          versionId,
          fileName,
          returnBlob: true,
        });
        if (result.cancelled) return;
        if (
          !result.success ||
          !result.blob ||
          result.versionId === undefined ||
          result.versionNumber === undefined
        ) {
          void messageApi.error(
            result.error?.message ?? `Failed to read ${fileName}`,
          );
          return;
        }
        const jobId = await startPdfIntake({
          file: new File([result.blob], fileName, { type: 'application/pdf' }),
          order,
          userId: user.uid,
          messageApi,
          source: {
            kind: 'attachment',
            fileUid,
            versionId: result.versionId,
            versionNumber: result.versionNumber,
            fileName,
          },
        });
        if (jobId) {
          const { router } = await import('@/router/router');
          await router.navigate(`/intake/schedules/${jobId}`);
        }
      } finally {
        setExtractingVersionId(null);
      }
    },
    [downloadAttachment, messageApi, user],
  );

  return { extractSchedule, extractingVersionId, canExtract };
};
